import { Fragment, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { getFloatDashboard, getFloatReservations, recheckSafaricomBalance } from '../../services/FloatService'
import { ROLE_SLUGS } from '../../utils/auth/permissionConstants'
import useGuidedTour from '../../hooks/useGuidedTour'
import { SECURITY_OUTRO_WITHOUT_PIN_TOUR_ID } from '../../hooks/faqOutroSteps'
import { FLOAT_HEALTH_TOUR_ID, buildFloatHealthTourSteps } from './FloatHealth.tour'

const DASHBOARD_REFRESH_INTERVAL_MS = 30_000
const RUNWAY_BAND_COLOURS = {
    green: { background: 'rgba(25, 135, 84, 0.15)', foreground: '#0f5132', label: 'Comfortable' },
    amber: { background: 'rgba(255, 193, 7, 0.20)', foreground: '#664d03', label: 'Getting tight' },
    red:   { background: 'rgba(220, 53, 69, 0.15)', foreground: '#842029', label: 'Act now' },
}
const ACCOUNT_LABELS = {
    mpesa_utility: 'Utility (send money)',
    mpesa_working: 'Working (paybill, till, pochi)',
}

const formatKes = (value) => {
    if (value === null || value === undefined) return '-'
    const asNumber = Number(value)
    if (Number.isNaN(asNumber)) return '-'
    return `KES ${asNumber.toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
}

const formatRunway = (minutes) => {
    if (minutes === null || minutes === undefined) return 'No recent activity'
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const remaining = minutes % 60
    if (hours < 24) return remaining === 0 ? `${hours} h` : `${hours} h ${remaining} m`
    const days = Math.floor(hours / 24)
    return `${days} day${days === 1 ? '' : 's'}+`
}

const formatIsoTimestamp = (iso) => {
    if (!iso) return null
    return new Date(iso).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })
}

const copyAmountToClipboard = async (amount) => {
    try {
        await navigator.clipboard.writeText(String(amount))
        toast.success('Amount copied to clipboard.')
    } catch {
        toast.error('Could not copy. Select the amount manually.')
    }
}

const RunwayBadge = ({ band }) => {
    const style = RUNWAY_BAND_COLOURS[band] ?? RUNWAY_BAND_COLOURS.green
    return (
        <span
            className="badge badge-rounded"
            style={{
                background: style.background,
                color: style.foreground,
                fontWeight: 600,
                fontSize: '0.72rem',
                padding: '0.35rem 0.7rem',
            }}
        >
            {style.label}
        </span>
    )
}

const RefreshingValue = ({ isRefreshing, children, spinnerSize = '0.85rem' }) => {
    if (!isRefreshing) return children
    return (
        <span className="d-inline-flex align-items-center text-muted" aria-live="polite">
            <span
                className="spinner-border"
                style={{ width: spinnerSize, height: spinnerSize, borderWidth: '0.12em' }}
                role="status"
                aria-hidden="true"
            />
            <span className="visually-hidden">Recomputing</span>
        </span>
    )
}

const AccountRunwayCard = ({ accountKey, account, onRecheckSafaricom, isRechecking, isRefreshing }) => {
    if (!account || !account.available) {
        return (
            <div className="card h-100">
                <div className="card-body text-center py-4">
                    <p className="text-muted mb-0">No data for {ACCOUNT_LABELS[accountKey] ?? accountKey} yet.</p>
                </div>
            </div>
        )
    }

    const band = account.runway_band ?? 'green'
    const style = RUNWAY_BAND_COLOURS[band]
    const hasRunway = account.runway_minutes !== null && account.runway_minutes !== undefined

    return (
        <div
            className="card h-100 shadow-sm border-0"
            style={{
                borderLeft: `4px solid ${style.foreground}`,
                borderRadius: 12,
                background: `linear-gradient(180deg, ${style.background} 0%, rgba(255,255,255,0) 55%)`,
            }}
        >
            <div className="card-body">
                <div className="d-flex align-items-start justify-content-between mb-3 gap-2">
                    <div>
                        <div className="text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                            {accountKey === 'mpesa_utility' ? 'Send money' : 'Paybill, till, pochi'}
                        </div>
                        <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 700 }}>
                            {accountKey === 'mpesa_utility' ? 'Utility Account' : 'Working Account'}
                        </h5>
                    </div>
                    <RunwayBadge band={band} />
                </div>

                {hasRunway ? (
                    <div className="mb-3">
                        <div style={{ fontSize: '2.25rem', fontWeight: 700, color: style.foreground, lineHeight: 1.05 }}>
                            <RefreshingValue isRefreshing={isRefreshing} spinnerSize="1.5rem">
                                {formatRunway(account.runway_minutes)}
                            </RefreshingValue>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                            at current burn of {formatKes(account.hourly_burn_1h)} per hour
                        </div>
                    </div>
                ) : (
                    <div className="mb-3">
                        <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Ledger balance
                        </div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: style.foreground, lineHeight: 1.1 }}>
                            <RefreshingValue isRefreshing={isRefreshing} spinnerSize="1.25rem">
                                {formatKes(account.ledger_balance)}
                            </RefreshingValue>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                            Runway shows up once the account has some outflow to measure.
                        </div>
                    </div>
                )}

                <div className="border-top pt-3" style={{ fontSize: '0.85rem' }}>
                    {hasRunway && (
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>account_balance_wallet</i>Ledger balance</span>
                            <RefreshingValue isRefreshing={isRefreshing}>
                                <span className="fw-bold" style={{ color: '#0f172a' }}>{formatKes(account.ledger_balance)}</span>
                            </RefreshingValue>
                        </div>
                    )}
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>schedule_send</i>In flight</span>
                        <RefreshingValue isRefreshing={isRefreshing}>
                            <span className="fw-semibold">{formatKes(account.in_flight_amount)}</span>
                        </RefreshingValue>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>verified</i>Daraja balance</span>
                        {isRechecking ? (
                            <span className="d-inline-flex align-items-center text-muted" style={{ fontSize: '0.8rem' }}>
                                <span className="spinner-border spinner-border-sm me-2" style={{ width: '0.85rem', height: '0.85rem' }} role="status" aria-hidden="true" />
                                Syncing
                            </span>
                        ) : account.daraja_balance
                            ? (
                                <RefreshingValue isRefreshing={isRefreshing}>
                                    <span className="fw-bold" style={{ color: style.foreground }}>{formatKes(account.daraja_balance)}</span>
                                </RefreshingValue>
                            )
                            : <span className="text-muted fw-normal">not yet synced</span>}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2 text-muted" style={{ fontSize: '0.7rem' }}>
                        <span>
                            {isRechecking
                                ? 'Syncing with Daraja'
                                : account.daraja_queried_at
                                    ? <>Last synced <span className="fw-semibold">{formatIsoTimestamp(account.daraja_queried_at)}</span></>
                                    : 'Never synced'}
                        </span>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ fontSize: '0.7rem' }}
                            onClick={onRecheckSafaricom}
                            disabled={isRechecking}
                            aria-label="Sync with Daraja"
                        >
                            {isRechecking ? (
                                <><span className="spinner-border spinner-border-sm me-1" style={{ width: '0.7rem', height: '0.7rem' }} role="status" aria-hidden="true" /> Syncing</>
                            ) : (
                                <><i className="material-icons align-middle me-1" style={{ fontSize: '0.85rem' }}>sync</i>Sync with Daraja</>
                            )}
                        </button>
                    </div>
                    {account.drift_over_tolerance && (
                        <div className="d-flex justify-content-between mb-0" style={{ color: RUNWAY_BAND_COLOURS.red.foreground }}>
                            <span><i className="material-icons align-middle me-1" style={{ fontSize: '1rem' }}>warning_amber</i>Drift over tolerance</span>
                            <RefreshingValue isRefreshing={isRechecking || isRefreshing}>
                                <span className="fw-semibold">{formatKes(account.drift)}</span>
                            </RefreshingValue>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const CARD_ACCENTS = {
    rebalance: { hex: '#c99509', bg: 'rgba(255, 193, 7, 0.14)', icon: 'compare_arrows', eyebrow: 'Action needed' },
    ok:        { hex: '#0f5132', bg: 'rgba(25, 135, 84, 0.12)', icon: 'check_circle',   eyebrow: 'All clear' },
    pool:      { hex: '#0d6efd', bg: 'rgba(13, 110, 253, 0.10)', icon: 'trending_up',   eyebrow: 'Incoming' },
    escrow:    { hex: '#6f42c1', bg: 'rgba(111, 66, 193, 0.10)', icon: 'schedule_send', eyebrow: 'Pending settlement' },
    blocked:   { hex: '#842029', bg: 'rgba(220, 53, 69, 0.10)',  icon: 'report',         eyebrow: 'Blocked' },
    trend:     { hex: '#0f6357', bg: 'rgba(32, 201, 151, 0.10)', icon: 'ssid_chart',    eyebrow: 'Last 24 hours' },
}

const CardShell = ({ accent, title, subtitle, hero, heroLabel, children, empty, isRefreshing = false }) => (
    <div
        className="card h-100 shadow-sm border-0"
        style={{
            borderRadius: 12,
            borderLeft: `4px solid ${accent.hex}`,
            background: `linear-gradient(180deg, ${accent.bg} 0%, rgba(255,255,255,0) 55%)`,
        }}
    >
        <div className="card-body">
            <div className="d-flex align-items-start justify-content-between mb-3 gap-2">
                <div>
                    <div className="text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                        {accent.eyebrow}
                    </div>
                    <h5 className="mb-0 d-flex align-items-center gap-2" style={{ fontSize: '1rem', fontWeight: 700 }}>
                        <i className="material-icons" style={{ fontSize: '1.15rem', color: accent.hex }}>{accent.icon}</i>
                        {title}
                    </h5>
                </div>
            </div>
            {subtitle && (
                <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>{subtitle}</p>
            )}
            {hero !== undefined && hero !== null && (
                <div className="mb-3">
                    {heroLabel && (
                        <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {heroLabel}
                        </div>
                    )}
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: accent.hex, lineHeight: 1.1 }}>
                        <RefreshingValue isRefreshing={isRefreshing} spinnerSize="1.25rem">{hero}</RefreshingValue>
                    </div>
                </div>
            )}
            {empty && (
                <div className="text-center py-2">
                    <i className="material-icons mb-2" style={{ fontSize: '2rem', color: accent.hex, opacity: 0.5 }}>{accent.icon}</i>
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{empty}</p>
                </div>
            )}
            {children}
        </div>
    </div>
)

const RebalanceCard = ({ rebalance }) => {
    const hasRecommendation = rebalance && rebalance.direction && Number(rebalance.amount) > 0
    if (!hasRecommendation) {
        return (
            <CardShell
                accent={CARD_ACCENTS.ok}
                title="Float is balanced"
                empty="No rebalance needed right now."
            />
        )
    }
    const arrow = rebalance.direction === 'utility_to_working' ? 'Utility to Working' : 'Working to Utility'
    return (
        <CardShell
            accent={CARD_ACCENTS.rebalance}
            title="Move float now"
            subtitle={rebalance.reason}
            hero={formatKes(rebalance.amount)}
            heroLabel={arrow}
        >
            <div className="d-flex flex-wrap gap-2 mt-1">
                <button type="button" className="btn btn-sm btn-primary" onClick={() => copyAmountToClipboard(rebalance.amount)}>
                    <i className="material-icons align-middle me-1" style={{ fontSize: '1rem' }}>content_copy</i>
                    Copy amount
                </button>
                <a
                    href="https://org.ke.m-pesa.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                >
                    Open Safaricom Portal
                </a>
                <a href="/fund-transfers" className="btn btn-sm btn-outline-secondary">
                    Log the transfer
                </a>
            </div>
        </CardShell>
    )
}

const PendingPoolCard = ({ pool, isRefreshing }) => {
    const total = Number(pool?.total_weighted ?? 0)
    const entries = pool?.entries ?? []
    return (
        <CardShell
            accent={CARD_ACCENTS.pool}
            title="Anticipated spend"
            subtitle={`Weighted top-ups from the last ${pool?.window_minutes ?? 60} minutes, half-life ${pool?.half_life_minutes ?? 30} minutes.`}
            hero={total > 0 ? formatKes(pool?.total_weighted ?? '0.00') : null}
            empty={total === 0 ? 'No fresh top-ups in the window.' : undefined}
            isRefreshing={isRefreshing}
        >
            {total > 0 && (
                <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                    {entries.slice(0, 5).map((entry, index) => (
                        <li key={index} className="d-flex justify-content-between border-top pt-2 mt-2">
                            <span>{formatKes(entry.amount)} <span className="text-muted">({entry.age_minutes} min ago)</span></span>
                            <span className="text-muted">weight {entry.weight.toFixed(2)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </CardShell>
    )
}

const InFlightEscrowCard = ({ escrow, isRefreshing }) => {
    if (!escrow) return null
    const openCount = Number(escrow.open_count ?? 0)
    if (openCount === 0) {
        return (
            <CardShell
                accent={CARD_ACCENTS.escrow}
                title="In-flight escrow"
                empty="Nothing in flight right now."
            />
        )
    }
    return (
        <CardShell
            accent={CARD_ACCENTS.escrow}
            title="In-flight escrow"
            subtitle="Money reserved for payouts sent to Safaricom but not yet settled."
            hero={formatKes(escrow.total_escrow_balance)}
            isRefreshing={isRefreshing}
        >
            <div className="border-top pt-3" style={{ fontSize: '0.85rem' }}>
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>call_made</i>Heading to utility</span>
                    <span className="fw-semibold">{formatKes(escrow.by_side?.utility ?? '0.00')}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>call_made</i>Heading to working</span>
                    <span className="fw-semibold">{formatKes(escrow.by_side?.working ?? '0.00')}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>list_alt</i>Open payouts</span>
                    <span className="fw-semibold">{openCount}</span>
                </div>
                {escrow.oldest_age_minutes !== null && escrow.oldest_age_minutes !== undefined && (
                    <div className="d-flex justify-content-between mb-0">
                        <span className="text-muted"><i className="material-icons align-middle text-muted me-1" style={{ fontSize: '1rem' }}>hourglass_bottom</i>Oldest wait</span>
                        <span className="fw-semibold">{escrow.oldest_age_minutes} min</span>
                    </div>
                )}
            </div>
        </CardShell>
    )
}

const ReservationsCard = ({ reservationsQuery }) => {
    const { data, isPending, isError, error } = reservationsQuery
    const rows = data?.data?.data ?? []
    const accent = rows.length > 0 ? CARD_ACCENTS.blocked : CARD_ACCENTS.ok

    if (isPending) {
        return (
            <CardShell accent={CARD_ACCENTS.blocked} title="Blocked payouts" subtitle="Payouts we could not send because float was short.">
                <div className="d-flex justify-content-center py-3">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
            </CardShell>
        )
    }
    if (isError) {
        return (
            <CardShell accent={CARD_ACCENTS.blocked} title="Blocked payouts">
                <p className="text-danger mb-0" style={{ fontSize: '0.85rem' }}>
                    Could not load reservations: {error?.message ?? 'unknown error'}
                </p>
            </CardShell>
        )
    }
    if (rows.length === 0) {
        return (
            <CardShell
                accent={accent}
                title="No blocked payouts"
                empty="Float has been covering every payout."
            />
        )
    }
    return (
        <CardShell
            accent={accent}
            title="Blocked payouts"
            subtitle="Payouts we could not send because float was short. Users were asked to try again."
            hero={String(rows.length)}
            heroLabel={rows.length === 1 ? 'blocked in the last day' : 'blocked in the last day'}
        >
            <ul className="list-group list-group-flush">
                {rows.map((reservation) => (
                    <li key={reservation.id} className="list-group-item px-0 bg-transparent">
                        <div className="d-flex justify-content-between align-items-start flex-wrap">
                            <div>
                                <div className="fw-semibold">{formatKes(reservation.amount)}</div>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    {ACCOUNT_LABELS[reservation.account_key] ?? reservation.account_key}
                                </div>
                            </div>
                            <div className="text-muted text-end" style={{ fontSize: '0.75rem' }}>
                                queued {formatIsoTimestamp(reservation.queued_at)}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </CardShell>
    )
}

const SparklineCard = ({ sparkline }) => {
    const accounts = Object.keys(sparkline ?? {}).filter((key) => (sparkline[key] ?? []).some((row) => Number(row.outflow) > 0))
    if (accounts.length === 0) {
        return (
            <CardShell
                accent={CARD_ACCENTS.trend}
                title="24 hour trend"
                empty="No activity in the last 24 hours."
            />
        )
    }

    return (
        <CardShell accent={CARD_ACCENTS.trend} title="24 hour trend">
            <div className="d-flex flex-column gap-4" style={{ maxWidth: 720, margin: '0 auto' }}>
                {accounts.map((accountKey) => (
                    <AccountHourlyBars key={accountKey} accountKey={accountKey} points={sparkline[accountKey]} />
                ))}
            </div>
        </CardShell>
    )
}

const HOURLY_CHART_HEIGHT_PX = 80
const HOURLY_TICK_EVERY_HOURS = 3

const formatHourLabel = (isoHourString) =>
    new Date(isoHourString).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })

const AccountHourlyBars = ({ accountKey, points }) => {
    const barColour = { mpesa_utility: '#0d6efd', mpesa_working: '#c99509' }[accountKey] ?? '#666'
    const values = points.map((row) => Number(row.outflow) || 0)
    const peak = Math.max(1, ...values)
    const total = values.reduce((acc, value) => acc + value, 0)
    const peakIndex = values.reduce((bestIndex, value, index) => (value > values[bestIndex] ? index : bestIndex), 0)
    const peakHour = formatHourLabel(points[peakIndex].hour)
    const lastIndex = points.length - 1

    // Tick anchors at the real clock hour of each bar. With a zero-filled
    // 24 point array ending on the current hour, this places labels at true
    // multiples of the tick interval and never drifts. The last slot is
    // reserved for "now" so the trailing tick does not collide with it.
    const xAxisTickIndexes = []
    for (let i = 0; i < points.length; i++) {
        if (i === lastIndex) continue
        const hour = new Date(points[i].hour).getHours()
        if (hour % HOURLY_TICK_EVERY_HOURS === 0) xAxisTickIndexes.push(i)
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-1">
                <div>
                    <div className="fw-semibold" style={{ color: barColour, fontSize: '0.9rem' }}>
                        {ACCOUNT_LABELS[accountKey] ?? accountKey}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        24h total {formatKes(total.toFixed(2))}
                    </div>
                </div>
                <div className="text-end">
                    <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Peak hour
                    </div>
                    <div className="fw-semibold" style={{ color: barColour, fontSize: '0.9rem' }}>
                        {formatKes(peak.toFixed(2))} at {peakHour}
                    </div>
                </div>
            </div>
            <div className="d-flex" style={{ gap: 8 }}>
                <div className="d-flex flex-column justify-content-between text-end text-muted" style={{ fontSize: '0.7rem', width: 60, height: HOURLY_CHART_HEIGHT_PX }}>
                    <span>{formatKes(peak.toFixed(0))}</span>
                    <span>KES 0</span>
                </div>
                <div className="flex-fill">
                    <div
                        role="img"
                        aria-label={`Hourly outflow for ${accountKey} over the last 24 hours`}
                        className="d-flex align-items-end"
                        style={{ height: HOURLY_CHART_HEIGHT_PX, gap: 2, borderBottom: '1px solid #d1d5db' }}
                    >
                        {values.map((value, index) => {
                            const heightPct = (value / peak) * 100
                            const isCurrent = points[index].is_current_hour ?? (index === lastIndex)
                            const isPeak = index === peakIndex && value > 0
                            const hourLabel = formatHourLabel(points[index].hour)
                            const suffix = isCurrent ? ' (in progress)' : ''
                            return (
                                <div key={index} className="position-relative" style={{ flex: 1, minWidth: 0, height: '100%' }}>
                                    {isPeak && (
                                        <div
                                            aria-hidden="true"
                                            style={{
                                                position: 'absolute',
                                                bottom: `calc(${heightPct}% + 4px)`,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                background: barColour,
                                            }}
                                        />
                                    )}
                                    <div
                                        title={`${hourLabel}${suffix}: ${formatKes(value.toFixed(2))}`}
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: `${heightPct}%`,
                                            background: barColour,
                                            opacity: isCurrent ? 0.35 : (isPeak ? 1 : 0.65),
                                            border: isCurrent ? `1px dashed ${barColour}` : (isPeak ? `1px solid ${barColour}` : 'none'),
                                            borderRadius: '2px 2px 0 0',
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>
                    <div className="position-relative text-muted" style={{ fontSize: '0.7rem', height: 16, marginTop: 4 }}>
                        {xAxisTickIndexes.map((index) => (
                            <span
                                key={index}
                                style={{
                                    position: 'absolute',
                                    left: `${((index + 0.5) / points.length) * 100}%`,
                                    transform: 'translateX(-50%)',
                                }}
                            >
                                {formatHourLabel(points[index].hour)}
                            </span>
                        ))}
                        <span style={{ position: 'absolute', right: 0, fontWeight: 600 }}>now</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

const FloatHealth = () => {
    const authUser = useSelector((state) => state.auth?.authUser ?? null)
    const userRole = authUser?.role_slug
    const completedTourIds = authUser?.completed_tour_ids ?? []
    const isExecAdmin = !!authUser?.id && userRole === ROLE_SLUGS.EXEC_ADMIN

    const dashboardQuery = useQuery({
        queryKey: ['float-dashboard'],
        queryFn: getFloatDashboard,
        staleTime: 0,
        refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
        enabled: isExecAdmin,
    })

    const reservationsQuery = useQuery({
        queryKey: ['float-reservations', 1],
        queryFn: () => getFloatReservations(1),
        staleTime: 0,
        refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
        enabled: isExecAdmin,
    })

    const queryClient = useQueryClient()
    const recheckSafaricom = useMutation({
        mutationFn: recheckSafaricomBalance,
        onSuccess: (response) => {
            toast.info(response?.data?.message ?? 'Balance check dispatched.')
            // Safaricom's async callback lands in about 10 to 15 seconds. Give
            // it a beat, then force-refresh the dashboard so the updated
            // snapshot and timestamp show up without a manual refresh.
            setTimeout(() => forceRefresh.mutate(), 12_000)
        },
        onError: (error) => {
            const message = error?.response?.data?.message ?? 'Could not trigger a Safaricom recheck.'
            toast.error(message)
        },
    })

    const forceRefresh = useMutation({
        mutationFn: () => getFloatDashboard({ force: true }),
        onSuccess: (response) => {
            queryClient.setQueryData(['float-dashboard'], response)
        },
        onError: (error) => {
            const message = error?.response?.data?.message ?? 'Could not refresh the dashboard.'
            toast.error(message)
        },
    })

    const floatHealthTourSteps = useMemo(
        () => buildFloatHealthTourSteps({ completedTourIds }),
        [completedTourIds]
    )
    useGuidedTour(FLOAT_HEALTH_TOUR_ID, floatHealthTourSteps, {
        enabled: isExecAdmin,
        alsoMarkCompleted: [SECURITY_OUTRO_WITHOUT_PIN_TOUR_ID],
    })

    if (!isExecAdmin) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (dashboardQuery.isPending) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <span className="spinner-border text-primary" role="status" />
                            <p className="text-muted mb-0 mt-3">Reading float health...</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (dashboardQuery.isError) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-danger mb-1">Could not load the float dashboard.</p>
                            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>{dashboardQuery.error?.message ?? 'Please retry in a moment.'}</p>
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => forceRefresh.mutate()}>
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const payload = dashboardQuery.data?.data ?? {}
    const accounts = payload.accounts ?? {}
    const isRefreshing = forceRefresh.isPending

    return (
        <Fragment>
            <div className="d-flex align-items-center justify-content-end mb-3">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => forceRefresh.mutate()}
                    disabled={isRefreshing}
                    aria-label="Refresh now"
                >
                    {isRefreshing ? (
                        <><span className="spinner-border spinner-border-sm me-2" style={{ width: '0.85rem', height: '0.85rem' }} role="status" aria-hidden="true" />Refreshing</>
                    ) : (
                        <><i className="material-icons align-middle me-1" style={{ fontSize: '1rem' }}>refresh</i>Refresh now</>
                    )}
                </button>
            </div>

            <div className="row g-3" data-tour="float-health-runway">
                <div className="col-12 col-md-6">
                    <AccountRunwayCard
                        accountKey="mpesa_utility"
                        account={accounts.mpesa_utility}
                        onRecheckSafaricom={() => recheckSafaricom.mutate()}
                        isRechecking={recheckSafaricom.isPending}
                        isRefreshing={isRefreshing}
                    />
                </div>
                <div className="col-12 col-md-6">
                    <AccountRunwayCard
                        accountKey="mpesa_working"
                        account={accounts.mpesa_working}
                        onRecheckSafaricom={() => recheckSafaricom.mutate()}
                        isRechecking={recheckSafaricom.isPending}
                        isRefreshing={isRefreshing}
                    />
                </div>
            </div>

            <div className="row g-3 mt-1">
                <div className="col-12 col-lg-6" data-tour="float-health-rebalance">
                    <RebalanceCard rebalance={payload.rebalance} />
                </div>
                <div className="col-12 col-lg-6">
                    <PendingPoolCard pool={payload.pending_pool} isRefreshing={isRefreshing} />
                </div>
            </div>

            <div className="row g-3 mt-1">
                <div className="col-12 col-lg-6" data-tour="float-health-in-flight">
                    <InFlightEscrowCard escrow={payload.in_flight_escrow} isRefreshing={isRefreshing} />
                </div>
                <div className="col-12 col-lg-6" data-tour="float-health-reservations">
                    <ReservationsCard reservationsQuery={reservationsQuery} />
                </div>
            </div>

            <div className="row g-3 mt-1 mb-3">
                <div className="col-12" data-tour="float-health-sparkline">
                    <SparklineCard sparkline={payload.sparkline} />
                </div>
            </div>
        </Fragment>
    )
}

export default FloatHealth
